import type {
  AuthTokens,
  LoginCredentials,
  RegisterData,
  User,
} from '../../types/models';
import type { ApiError } from './client';
import type { Result } from '../../types/common';
import { ok, err } from '../../utils/fp-utils';

const isBrowser = typeof window !== 'undefined';
const MOCK_USER_KEY = 'mock_auth_user';

const readMockUser = (): User | null => {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(MOCK_USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

const writeMockUser = (user: User): void => {
  if (!isBrowser) return;
  localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
};

const clearMockUser = (): void => {
  if (!isBrowser) return;
  localStorage.removeItem(MOCK_USER_KEY);
};

const toNameParts = (email: string): { firstName: string; lastName: string } => {
  const localPart = email.split('@')[0] || 'student.user';
  const [first = 'Student', last = 'User'] = localPart
    .split(/[._-]+/)
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''));
  return { firstName: first, lastName: last };
};

const createMockTokens = (email: string): AuthTokens => {
  const seed = btoa(`${email}:${Date.now()}`);
  return {
    accessToken: `mock_access_${seed}`,
    refreshToken: `mock_refresh_${seed}`,
    expiresIn: 60 * 60,
  };
};

const createMockUser = (
  email: string,
  firstName?: string,
  lastName?: string
): User => {
  const fallback = toNameParts(email);
  return {
    id: `user_${email.toLowerCase().replace(/[^a-z0-9]/g, '_')}`,
    email,
    firstName: firstName?.trim() || fallback.firstName,
    lastName: lastName?.trim() || fallback.lastName,
    avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200',
    role: 'student',
    subscriptionStatus: 'trial',
    createdAt: new Date().toISOString(),
  };
};

const hasStoredToken = (): boolean => {
  if (!isBrowser) return false;
  return Boolean(localStorage.getItem('access_token'));
};

export const mockAuth = {
  login: (credentials: LoginCredentials): Result<AuthTokens, ApiError> => {
    if (!credentials.email || !credentials.password) {
      return err({ message: 'Email and password are required', status: 400 });
    }
    if (credentials.password.length < 6) {
      return err({ message: 'Password must be at least 6 characters', status: 400 });
    }

    const user = createMockUser(credentials.email);
    const tokens = createMockTokens(credentials.email);
    writeMockUser(user);
    return ok(tokens);
  },

  register: (data: RegisterData): Result<AuthTokens, ApiError> => {
    if (!data.email || !data.password || !data.firstName || !data.lastName) {
      return err({ message: 'All registration fields are required', status: 400 });
    }
    if (data.password.length < 8) {
      return err({ message: 'Password must be at least 8 characters', status: 400 });
    }

    const user = createMockUser(data.email, data.firstName, data.lastName);
    const tokens = createMockTokens(data.email);
    writeMockUser(user);
    return ok(tokens);
  },

  me: (): Result<User, ApiError> => {
    if (!hasStoredToken()) {
      return err({ message: 'Unauthorized', status: 401 });
    }

    const user = readMockUser();
    if (user) {
      return ok(user);
    }

    return err({ message: 'User not found', status: 404 });
  },

  logout: (): Result<{ success: boolean }, ApiError> => {
    clearMockUser();
    return ok({ success: true });
  },

  refreshToken: (refreshToken: string): Result<AuthTokens, ApiError> => {
    if (!refreshToken) {
      return err({ message: 'Refresh token required', status: 400 });
    }
    const user = readMockUser();
    if (!user) {
      return err({ message: 'Unauthorized', status: 401 });
    }
    return ok(createMockTokens(user.email));
  },
};
